import * as React from "react";
import { TRIAL_CONTACT_COUNT, TRIAL_CONTACTS_PHRASE } from "@/lib/trial";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useAuth } from "@/hooks/use-auth";
import { getMyCredits, getMyProfile } from "@/lib/account.functions";
import { creditsView, showTrialStillAvailableLine } from "@/lib/credits-display";
import { listMyDeals, type DealSummary } from "@/lib/deals.functions";
import { ensureRankingDemoDeal } from "@/lib/ranking-demo.functions";
import { cn } from "@/lib/utils";
import { useOnboarding } from "@/lib/onboarding";
import { track } from "@/lib/track";

import { DealRow } from "@/components/dashboard/deal-row";
import {
  listDealNextStepInputs,
  listDealLatestActivity,
  listDealEngagedContacts,
  listDealAgentActionSummaries,
} from "@/lib/agent-actions.functions";
import { listRecentCompletedReports } from "@/lib/deal-reports.functions";
import {
  getRecentReplySummary,
  getUpcomingMeetingsSummary,
} from "@/lib/inbound-replies.functions";
import { TodayStrip } from "@/components/dashboard/today-strip";
import { listDealPlanSummaries } from "@/lib/deal-plans.functions";
import { decideSingleDealLanding } from "@/lib/single-deal-landing";
import { getPendingOnboarding } from "@/lib/onboarding-handoff.functions";
import { dashboardLine } from "@/lib/finder-handoff";


interface DashboardSearch {
  all?: "1";
}

export const Route = createFileRoute("/_authed/dashboard")({
  validateSearch: (search: Record<string, unknown>): DashboardSearch => {
    return search.all === "1" || search.all === 1 ? { all: "1" } : {};
  },
  head: () => ({
    meta: [
      { title: "Dashboard: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: DashboardPage,
});

function greetingForNow(date: Date): string {
  const hour = date.getHours();
  if (hour < 5) return "Good evening";
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

function DashboardPage() {
  const { user } = useAuth();
  const fetchCredits = useServerFn(getMyCredits);
  const fetchProfile = useServerFn(getMyProfile);
  const fetchDeals = useServerFn(listMyDeals);

  // Client-only: avoid SSR/client hydration mismatch from server (UTC) vs
  // client timezone landing in different greeting buckets (React #418).
  const [greeting, setGreeting] = React.useState<string>("");
  React.useEffect(() => {
    setGreeting(greetingForNow(new Date()));
  }, []);

  const credits = useQuery({
    queryKey: ["my-credits", user?.id],
    queryFn: () => fetchCredits(),
    enabled: !!user,
  });
  const profile = useQuery({
    queryKey: ["my-profile", user?.id],
    queryFn: () => fetchProfile(),
    enabled: !!user,
  });
  const deals = useQuery({
    queryKey: ["my-recent-deals", user?.id],
    queryFn: () => fetchDeals({ data: {} }),
    enabled: !!user,
    refetchOnWindowFocus: true,
    refetchOnMount: "always",
  });

  // Homepage Top 10 finder selections kept from before signup, shown once
  // until a deal picks them up.
  const fetchPendingOnboarding = useServerFn(getPendingOnboarding);
  const pendingOnboarding = useQuery({
    queryKey: ["pending-onboarding", user?.id],
    queryFn: () => fetchPendingOnboarding(),
    enabled: !!user,
    staleTime: 60_000,
  });
  const keptSearch =
    pendingOnboarding.data && !pendingOnboarding.data.consumed
      ? pendingOnboarding.data.handoff
      : null;

  // 30-day plans: one batched load for all team deals.
  const fetchPlanSummaries = useServerFn(listDealPlanSummaries);
  const planSummaries = useQuery({
    queryKey: ["deal-plan-summaries"],
    queryFn: () => fetchPlanSummaries({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Recommended next steps: one batched load for all team deals.
  const fetchNextStepInputs = useServerFn(listDealNextStepInputs);
  const nextStepInputs = useQuery({
    queryKey: ["deal-next-step-inputs", user?.id],
    queryFn: () => fetchNextStepInputs({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Latest activity: one batched load for all team deals.
  const fetchEngagedContacts = useServerFn(listDealEngagedContacts);
  const engagedContacts = useQuery({
    queryKey: ["deal-engaged-contacts"],
    queryFn: () => fetchEngagedContacts({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const fetchLatestActivity = useServerFn(listDealLatestActivity);
  const latestActivity = useQuery({
    queryKey: ["deal-latest-activity", user?.id],
    queryFn: () => fetchLatestActivity({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Cross-deal Today strip inputs.
  const fetchActionSummaries = useServerFn(listDealAgentActionSummaries);
  const actionSummaries = useQuery({
    queryKey: ["deal-action-summaries", user?.id],
    queryFn: () => fetchActionSummaries({}),
    enabled: !!user,
    staleTime: 30_000,
  });
  const fetchRecentReports = useServerFn(listRecentCompletedReports);
  const recentReports = useQuery({
    queryKey: ["recent-completed-reports", user?.id],
    queryFn: () => fetchRecentReports({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Investor replies received in the last 24 hours across team deals.
  const fetchReplySummary = useServerFn(getRecentReplySummary);
  const replySummary = useQuery({
    queryKey: ["recent-reply-summary", user?.id],
    queryFn: () => fetchReplySummary({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  // Meetings agreed with investors, in the next 7 days.
  const fetchMeetingSummary = useServerFn(getUpcomingMeetingsSummary);
  const meetingSummary = useQuery({
    queryKey: ["upcoming-meetings-summary", user?.id],
    queryFn: () => fetchMeetingSummary({}),
    enabled: !!user,
    staleTime: 30_000,
  });

  const today = React.useMemo(() => {
    const byDeal = actionSummaries.data?.byDealId ?? {};
    let followupCount = 0;
    let followupDealCount = 0;
    for (const v of Object.values(byDeal)) {
      const n = v?.open_count ?? 0;
      if (n > 0) {
        followupCount += n;
        followupDealCount += 1;
      }
    }
    const nextByDeal = nextStepInputs.data?.byDealId ?? {};
    let staleCount = 0;
    let staleDealId: string | null = null;
    let topStale = 0;
    for (const [dealId, v] of Object.entries(nextByDeal)) {
      const n = v?.stale_awaiting_count ?? 0;
      staleCount += n;
      if (n > topStale) {
        topStale = n;
        staleDealId = dealId;
      }
    }
    return { followupCount, followupDealCount, staleCount, staleDealId };
  }, [actionSummaries.data, nextStepInputs.data]);








  // One-time ranking demo deal for users who signed up from a /lists
  // ranking page. No-op (and no write) for everyone else.
  const ensureDemo = useServerFn(ensureRankingDemoDeal);
  const qc = useQueryClient();
  const demoRan = React.useRef(false);
  React.useEffect(() => {
    if (!user || demoRan.current) return;
    demoRan.current = true;
    void ensureDemo({})
      .then((res) => {
        if (res?.created) {
          void qc.invalidateQueries({ queryKey: ["my-recent-deals", user.id] });
        }
      })
      .catch(() => {});
  }, [user, ensureDemo, qc]);


  const displayName =
    profile.data?.first_name?.trim() ||
    (user?.email ? user.email.split("@")[0] : "there");

  const remaining = credits.data?.credits_remaining ?? 0;
  const total = credits.data?.credits_total_this_cycle ?? 0;
  const used = Math.max(0, total - remaining);
  const pct = total > 0 ? Math.min(100, Math.round((remaining / total) * 100)) : 0;
  const trialAvailable = credits.data?.trial_available ?? false;
  const canRun = remaining > 0 || trialAvailable;
  const view = creditsView(remaining, trialAvailable);
  const trialStillAvailable = showTrialStillAvailableLine(remaining, trialAvailable);

  // Onboarding flags (get-started checklist dismissal and related).
  const onboarding = useOnboarding();

  // Single-deal landing: a user with exactly one real deal goes straight to
  // that deal's Overview. all=1 keeps them on the list.
  const { all } = Route.useSearch();
  const navigate = useNavigate();
  const landed = React.useRef(false);
  React.useEffect(() => {
    if (landed.current) return;
    if (onboarding.isLoading || !deals.data) return;
    const target = decideSingleDealLanding({
      deals: deals.data,
      all: all === "1",
      blocked:
        !onboarding.getting_started_dismissed_at && checklistIncomplete(deals.data),
    });
    if (!target) return;
    landed.current = true;
    void navigate({ to: "/deals/$dealId", params: { dealId: target }, replace: true });
  }, [
    all,
    deals.data,
    navigate,
    onboarding.isLoading,
    onboarding.getting_started_dismissed_at,
  ]);


  return (
    <main className="mx-auto max-w-[640px] px-4 pb-16 pt-6 lg:max-w-[1320px]">
      <div className="mb-3">
        <a
          href="https://demo.capitaldart.com"
          target="_blank"
          rel="noopener"
          aria-label="Watch the interactive demo in a new tab"
          onClick={() => track("demo_start", { cta_placement: "dashboard-header" })}
          className="inline-block rounded border border-brand-line px-2 py-1 text-[12px] text-brand-ink hover:bg-brand-paper-2"
        >
          Watch the interactive demo <span aria-hidden>↗</span>
        </a>
      </div>
      <section className="mb-6">
        <p className="min-h-[1em] text-[14px] text-brand-muted">{greeting}</p>
        <h1 className="font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
          {displayName}.
        </h1>
      </section>

      <GetStartedChecklist
        deals={deals.data}
        onboardingLoading={onboarding.isLoading}
        dismissedAt={onboarding.getting_started_dismissed_at}
        onDismiss={() => void onboarding.markSeen("getting_started_dismissed_at")}
      />

      <section
        aria-labelledby="credits-heading"
        className="mb-6 rounded-2xl border border-brand-ink/10 bg-brand-ink p-5 text-brand-paper shadow-[0_30px_60px_-30px_oklch(0.264_0.068_255.3/0.35)]"
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <p
                id="credits-heading"
                className="cursor-help text-[12px] font-medium uppercase tracking-[0.18em] text-brand-paper/60"
              >
                {view === "trial" ? "Free trial" : "Credits remaining"}
              </p>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="max-w-[260px] text-center text-[12px] leading-relaxed">
                One credit delivers one matched investor, with the outreach drafted in your voice.
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {view === "trial" ? (
          <div className="mt-1 space-y-1">
            <div className="font-serif text-[26px] leading-tight">
              Free trial: {TRIAL_CONTACT_COUNT} matched contacts available
            </div>
          </div>
        ) : (
          <div className="mt-1 space-y-1">
            <div className="flex items-baseline gap-2">
              <span className="font-serif text-[44px] leading-none tabular-nums">
                {credits.data ? remaining : "—"}
              </span>
              <span className="text-[15px] text-brand-paper/55">
                / {credits.data ? total : "—"}
              </span>
            </div>
            {trialStillAvailable && (
              <p className="text-[13px] text-brand-paper/55">
                Your {TRIAL_CONTACT_COUNT} free trial contacts are still available on your
                first delivery.
              </p>
            )}
          </div>
        )}
        <div
          className="mt-4 h-1.5 w-full overflow-hidden rounded-full bg-brand-paper/15"
          role="progressbar"
          aria-valuenow={pct}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="h-full rounded-full bg-brand-accent transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        <div className="mt-3 flex items-center justify-between">
          <span className="text-[13px] text-brand-paper/60">
            {credits.data ? `${used} used this cycle` : "Loading…"}
          </span>
          <Link
            to="/buy-credits"
            className="text-[14px] font-medium text-brand-accent no-underline hover:text-brand-paper"
          >
            Top up <span aria-hidden>→</span>
          </Link>
        </div>
      </section>

      {keptSearch ? (
        <section className="mb-4">
          <p className="text-[14px] text-brand-muted">
            {dashboardLine(keptSearch)}{" "}
            <Link
              to="/app"
              search={{ finder: "1" } as never}
              className="font-medium text-brand-accent no-underline hover:text-brand-ink"
            >
              Start a deal <span aria-hidden>→</span>
            </Link>
          </p>
        </section>
      ) : null}

      <section className="mb-8">
        <div>
          <Button asChild className="w-full" disabled={credits.data != null && !canRun}>
            <Link to="/app">
              {view === "trial" ? "Try a free sample " : "Start a new deal "}
              <span aria-hidden>→</span>
            </Link>
          </Button>
        </div>
        {view === "trial" && (
          <p className="mt-3 text-center text-[14px] text-brand-muted">
            Free trial: {TRIAL_CONTACTS_PHRASE}, outreach drafts, and a free Investor
            Readiness Report. No credit card.
          </p>
        )}
      </section>

      <TodayStrip
        replyCount={replySummary.data?.count ?? 0}
        replyDealCount={replySummary.data?.dealCount ?? 0}
        replyDealId={replySummary.data?.topDealId ?? null}
        followupCount={today.followupCount}
        followupDealCount={today.followupDealCount}
        staleCount={today.staleCount}
        staleDealId={today.staleDealId}
        meetingCount={meetingSummary.data?.count ?? 0}
        meetingDealId={meetingSummary.data?.soonestDealId ?? null}
        reports={recentReports.data?.reports ?? []}
      />

      <section aria-labelledby="recent-heading">
        <header className="mb-3 flex items-center gap-3">
          <span aria-hidden className="h-px w-6 bg-brand-accent" />
          <span className="font-serif text-sm italic text-brand-accent">01</span>
          <h2
            id="recent-heading"
            className="text-[14px] font-semibold uppercase tracking-[0.18em] text-brand-ink"
          >
            Recent deals
          </h2>
        </header>

        {deals.isLoading ? (
          <DealsListSkeleton />
        ) : deals.isError ? (
          <ErrorRow message={deals.error instanceof Error ? deals.error.message : "Failed to load deals"} onRetry={() => deals.refetch()} />
        ) : !deals.data || deals.data.length === 0 ? (
          <EmptyDeals />
        ) : (
          <ul className="space-y-2">
            {deals.data.map((deal) => (
              <li key={deal.id}>
              <DealRow
                deal={deal}
                nextStepInputs={
                  nextStepInputs.data?.byDealId?.[deal.id] ?? null
                }
                activity={
                  latestActivity.data?.byDealId?.[deal.id] ?? null
                }
                engaged={
                  engagedContacts.data?.byDealId?.[deal.id] ?? null
                }
                planSummary={planSummaries.data?.byDealId?.[deal.id]}
              />
              </li>
            ))}
          </ul>
        )}
      </section>
    </main>
  );
}


/** True while the get started checklist still has an unfinished step. */
function checklistIncomplete(deals: DealSummary[]): boolean {
  const hasDeal = deals.length > 0;
  const hasIrr = deals.some((d) => d.latest_complete_report_score != null);
  const hasContacts = deals.some((d) => {
    const total = Object.values(d.status_counts ?? {}).reduce(
      (a, n) => a + (Number(n) || 0),
      0,
    );
    return total > 0;
  });
  return !(hasDeal && hasIrr && hasContacts);
}

function GetStartedChecklist({
  deals,
  onboardingLoading,
  dismissedAt,
  onDismiss,
}: {
  deals: DealSummary[] | undefined;
  onboardingLoading: boolean;
  dismissedAt: string | null;
  onDismiss: () => void;
}) {
  if (onboardingLoading) return null;
  if (dismissedAt) return null;
  if (!deals) return null;
  if (!checklistIncomplete(deals)) return null;

  const hasDeal = deals.length > 0;
  const hasIrr = deals.some((d) => d.latest_complete_report_score != null);
  const hasContacts = deals.some((d) => {
    const total = Object.values(d.status_counts ?? {}).reduce(
      (a, n) => a + (Number(n) || 0),
      0,
    );
    return total > 0;
  });

  // Most-recent deal for steps 2 and 3 (deals.data is sorted by updated_at desc).
  const firstDealId = deals[0]?.id ?? null;

  const steps: Array<{
    label: string;
    done: boolean;
    to?: { to: string; params?: Record<string, string> };
  }> = [
    {
      label: "Create your first deal",
      done: hasDeal,
      to: hasDeal ? undefined : { to: "/app" },
    },
    {
      label: "Run your Investor Readiness Report (free)",
      done: hasIrr,
      to:
        hasIrr || !firstDealId
          ? undefined
          : { to: "/deals/$dealId/contacts", params: { dealId: firstDealId } },
    },
    {
      label: "Review your investor matches",
      done: hasContacts,
      to:
        hasContacts || !firstDealId
          ? undefined
          : { to: "/deals/$dealId/contacts", params: { dealId: firstDealId } },
    },
  ];

  return (
    <section className="mb-6 rounded-2xl border border-brand-line bg-brand-paper p-5">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <p className="text-[12px] font-semibold uppercase tracking-[0.18em] text-brand-ink">
            Get started
          </p>
          <p className="mt-1 text-[14px] text-brand-muted">
            A few steps to take your first deal to market.
          </p>
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-[12px] text-brand-muted hover:text-brand-ink"
        >
          Dismiss
        </button>
      </div>
      <ul className="space-y-2">
        {steps.map((s) => {
          const Icon = s.done ? CheckCircle2 : Circle;
          const iconClass = s.done
            ? "text-brand-ok"
            : "text-brand-line";
          const labelClass = s.done
            ? "text-brand-muted"
            : "text-brand-ink";
          const content = (
            <span className={cn("flex items-center gap-2.5 text-[14px]", labelClass)}>
              <Icon className="size-4 shrink-0" aria-hidden />
              <span>{s.label}</span>
            </span>
          );
          if (!s.done && s.to) {
            return (
              <li key={s.label}>
                <Link
                  to={s.to.to}
                  params={s.to.params as never}
                  className="block no-underline hover:underline"
                >
                  {content}
                </Link>
              </li>
            );
          }
          return <li key={s.label}>{content}</li>;
        })}
      </ul>
    </section>
  );
}

function EmptyDeals() {
  return (
    <div className="rounded-xl border border-dashed border-brand-line bg-brand-paper px-4 py-8 text-center">
      <p className="text-[15px] text-brand-muted">
        No deals yet. Take a deal to market: drop your materials, we match investors and acquirers and write the outreach.
      </p>
    </div>
  );
}

function ErrorRow({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div className="rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-4 text-center">
      <p className="text-[14px] text-destructive">Couldn't load deals: {message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="mt-2 text-[14px] font-medium text-brand-accent underline-offset-2 hover:underline"
      >
        Retry
      </button>
    </div>
  );
}

function DealsListSkeleton() {
  return (
    <ul className="space-y-2">
      {[0, 1, 2].map((i) => (
        <li
          key={i}
          className="h-[58px] animate-pulse rounded-xl border border-brand-line bg-brand-paper"
        />
      ))}
    </ul>
  );
}